import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Check, AlertCircle, Clock, Tag, Calendar, X, Ticket, CheckCircle } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';

const ListingCreditsTab = () => {
  const [packages, setPackages] = useState([]);
  const [creditStatus, setCreditStatus] = useState(null);
  const [creditHistory, setCreditHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modal and coupon states
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(null);
  const [finalPrice, setFinalPrice] = useState(0);
  const [applyingCoupon, setApplyingCoupon] = useState(false);

  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  useEffect(() => {
    fetchPackages();
    fetchCreditStatus();
    fetchCreditHistory();
  }, []);

  const fetchPackages = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/credit-packages`);
      if (response.ok) {
        const data = await response.json();
        setPackages(data.packages || []);
      }
    } catch (error) {
      console.error('Error fetching packages:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCreditStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/listing-credits/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCreditStatus(data);
      }
    } catch (error) {
      console.error('Error fetching credit status:', error);
    }
  };

  const fetchCreditHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/listing-credits/history`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCreditHistory(data.history || []);
      }
    } catch (error) {
      console.error('Error fetching credit history:', error);
    }
  };

  const navigate = useNavigate();
  
  const handleSelectPackage = (pkg) => {
    // Open modal for coupon and confirmation
    setSelectedPackage(pkg);
    setFinalPrice(pkg.price);
    setCouponCode('');
    setCouponApplied(null);
    setShowPurchaseModal(true);
  };
  
  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      toast({ title: 'Error', description: 'Please enter a coupon code', variant: 'destructive' });
      return;
    }
    
    setApplyingCoupon(true);
    try {
      const response = await fetch(`${API_BASE}/api/coupons/validate?code=${couponCode}&amount=${selectedPackage.price}&coupon_type=listing`, {
        method: 'POST'
      });
      
      if (response.ok) {
        const data = await response.json();
        setCouponApplied(data.coupon);
        const discountedPrice = selectedPackage.price - data.coupon.discount_amount;
        setFinalPrice(Math.max(0, discountedPrice));
        toast({
          title: 'Coupon Applied! 🎉',
          description: `You save $${data.coupon.discount_amount.toFixed(2)}`,
        });
      } else {
        const error = await response.json();
        toast({ title: 'Invalid Coupon', description: error.detail || 'This coupon is not valid for listing credits', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to apply coupon', variant: 'destructive' });
    } finally {
      setApplyingCoupon(false);
    }
  };
  
  const handleRemoveCoupon = () => {
    setCouponCode('');
    setCouponApplied(null);
    if (selectedPackage) {
      setFinalPrice(selectedPackage.price);
    }
  };
  
  const handleConfirmPurchase = () => {
    if (!selectedPackage) return;
    
    // Navigate to payment page with package details and coupon info
    navigate('/payment', { 
      state: { 
        package: {
          ...selectedPackage,
          package_id: selectedPackage.package_id || selectedPackage._id || selectedPackage.id,
          type: 'listing_credit',
          currency: selectedPackage.currency || 'CAD',
          couponCode: couponApplied ? couponCode : null,
          couponApplied: couponApplied,
          originalPrice: selectedPackage.price,  // Keep original price
          price: selectedPackage.price  // Original price (discount handled separately)
        }
      } 
    });
    
    setShowPurchaseModal(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Credit Status Banner */}
      {creditStatus && creditStatus.has_active_credit && (
        <div className={`rounded-lg border ${creditStatus.show_warning ? 'bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border-amber-200 dark:border-amber-700' : 'bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-green-200 dark:border-green-700'}`}>
          <div className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className={`text-lg font-semibold ${creditStatus.show_warning ? 'text-amber-900 dark:text-amber-300' : 'text-green-900 dark:text-green-300'}`}>
                  Active Listing Credit
                </h3>
                <p className={`mt-1 ${creditStatus.show_warning ? 'text-amber-700 dark:text-amber-400' : 'text-green-700 dark:text-green-400'}`}>
                  {creditStatus.days_remaining} days remaining
                </p>
                <p className={`text-sm mt-2 ${creditStatus.show_warning ? 'text-amber-600 dark:text-amber-400' : 'text-green-600 dark:text-green-400'}`}>
                  Expires on: {formatDate(creditStatus.end_date)}
                </p>
              </div>
              <Badge className={`${creditStatus.show_warning ? 'bg-amber-500' : 'bg-green-500'} text-white px-4 py-2`}>
                <Clock className="w-4 h-4 mr-1" />
                Active
              </Badge>
            </div>
          </div>
        </div>
      )}

      {/* Warning if credit expiring soon */}
      {creditStatus && creditStatus.show_warning && (
        <div className="rounded-lg border bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-700">
          <div className="p-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5" />
              <div>
                <h4 className="font-semibold text-amber-900 dark:text-amber-300">Credit Expiring Soon!</h4>
                <p className="text-sm text-amber-700 dark:text-amber-400 mt-1">
                  Your listing credit will expire in {creditStatus.days_remaining} days. Purchase a new credit to stay listed on the homepage.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* No Credit Warning */}
      {creditStatus && !creditStatus.has_active_credit && (
        <div className="rounded-lg border bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700">
          <div className="p-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5" />
              <div>
                <h4 className="font-semibold text-red-900 dark:text-red-300">No Active Listing Credit</h4>
                <p className="text-sm text-red-700 dark:text-red-400 mt-1">
                  You are currently not listed on the homepage. Purchase a listing credit to get more visibility and attract clients.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Info Card */}
      <div className="rounded-lg border bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700">
        <div className="p-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
            <div>
              <h4 className="font-semibold text-blue-900 dark:text-blue-300">About Listing Credits</h4>
              <p className="text-sm text-blue-700 dark:text-blue-400 mt-1">
                Listing credits allow you to appear on the homepage where potential clients can discover you. 
                Without an active credit, your profile won't be visible in the public trainer listings.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Credit Packages */}
      <div>
        <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Purchase Listing Credit</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {packages.map((pkg) => (
            <div key={pkg.package_id} className="hover:shadow-lg transition-shadow border-2 border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800">
              <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white">{pkg.name}</h4>
              </div>
              <div className="p-4 space-y-4">
                <div>
                  <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                    ${pkg.price} <span className="text-base font-normal text-gray-500 dark:text-gray-400">CAD</span>
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{pkg.duration_days} days</p>
                </div>
                <div>
                  <p className="text-sm text-gray-700 dark:text-gray-300">{pkg.description}</p>
                </div>
                <Button 
                  onClick={() => handleSelectPackage(pkg)}
                  className="w-full"
                >
                  Select Package
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Credit History */}
      {creditHistory.length > 0 && (
        <div>
          <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Credit History</h3>
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
            <div className="p-6">
              <div className="space-y-3">
                {creditHistory.map((credit) => (
                  <div 
                    key={credit.credit_id}
                    className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600"
                  >
                    <div>
                      <p className="font-semibold text-gray-900 dark:text-white">{credit.package_name}</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {formatDate(credit.start_date)} - {formatDate(credit.end_date)}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {credit.payment_method === 'admin_grant' ? 'Admin Grant (Free)' : `Paid: $${credit.amount_paid} CAD`}
                      </p>
                    </div>
                    <Badge variant={credit.status === 'active' ? 'default' : 'secondary'}>
                      {credit.status}
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Purchase Modal with Coupon */}
      <Dialog open={showPurchaseModal} onOpenChange={setShowPurchaseModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-600" />
              Confirm Purchase
            </DialogTitle>
            <DialogDescription>
              Review your listing credit purchase details
            </DialogDescription>
          </DialogHeader>
          
          {selectedPackage && (
            <div className="space-y-4 py-4">
              {/* Package Info */}
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-700">
                <h4 className="font-semibold text-gray-900 dark:text-white">{selectedPackage.name}</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{selectedPackage.description}</p>
                <div className="flex items-center gap-2 mt-2">
                  <Clock className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  <span className="text-sm text-gray-600 dark:text-gray-400">{selectedPackage.duration_days} days</span>
                </div>
              </div>
              
              {/* Coupon Section */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                  <Ticket className="w-4 h-4" />
                  Have a Coupon Code?
                </label>
                
                {!couponApplied ? (
                  <div className="flex gap-2">
                    <Input
                      placeholder="Enter coupon code"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      className="flex-1 uppercase"
                    />
                    <Button 
                      onClick={handleApplyCoupon}
                      variant="outline"
                      disabled={applyingCoupon || !couponCode.trim()}
                    >
                      {applyingCoupon ? 'Applying...' : 'Apply'}
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between bg-green-50 dark:bg-green-900/20 p-3 rounded-lg border border-green-200 dark:border-green-700">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                      <div>
                        <p className="font-medium text-green-800 dark:text-green-300">{couponCode}</p>
                        <p className="text-sm text-green-600 dark:text-green-400">
                          -{couponApplied.discount_type === 'percentage' 
                            ? `${couponApplied.discount_value}%` 
                            : `$${couponApplied.discount_amount.toFixed(2)}`}
                        </p>
                      </div>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={handleRemoveCoupon}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
              
              {/* Price Summary */}
              <div className="border-t pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Package Price:</span>
                  <span className={`${couponApplied ? 'line-through text-gray-400' : 'text-gray-900 dark:text-white'}`}>
                    ${selectedPackage.price.toFixed(2)} CAD
                  </span>
                </div>
                
                {couponApplied && (
                  <div className="flex justify-between text-sm text-green-600 dark:text-green-400">
                    <span>Discount:</span>
                    <span>-${couponApplied.discount_amount.toFixed(2)} CAD</span>
                  </div>
                )}
                
                <div className="flex justify-between font-bold text-lg border-t pt-2">
                  <span className="text-gray-900 dark:text-white">Total:</span>
                  <span className="text-blue-600 dark:text-blue-400">${finalPrice.toFixed(2)} CAD</span>
                </div>
                
                {couponApplied && (
                  <p className="text-xs text-green-600 dark:text-green-400 text-right">
                    🎉 You save ${couponApplied.discount_amount.toFixed(2)}!
                  </p>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowPurchaseModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleConfirmPurchase} className="bg-blue-600 hover:bg-blue-700">
              Proceed to Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ListingCreditsTab;
